import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { SelectedTrips } from '../../BookingWizardWrap';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { TripSelectionCalendar } from './TripSelectionCalendar';
import TripOption from './TripOption/TripOption';
import SelectedFareTrip from './SelectedFareTrip';
import TripLegSummary from './TripLegSummary';
import { passengerCountsByType } from '../../BookingWizardData/BaseBookingWizardData';
import { appConfigStore } from 'Models/AppConfigStore';
import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import If from 'Views/Components/If/If';
import ErrorMessage from 'Views/Components/New/ErrorMessage';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useClearStaleTicket } from './Hooks/useClearStaleTicket';

interface DepartureTripSectionProps {
	wizardData: BookingWizardData;
	startLocation: { name: string } | undefined;
	endLocation: { name: string } | undefined;
	selectedDepartingTrip: TicketsTabTrip | undefined;
	trips: TicketsTabTrip[] | null;
	selectedTrips: SelectedTrips;
	isStaff: boolean;
	priceByFerryTrip: { [key: string]: number };
	classPricesByFerryTrip: { [key: string]: TripClassPrice[] };
	/**
	 * Whether the class fares are still being fetched.
	 *
	 * <p>Nothing is drawn until they land. Without it the step reads an empty map as "this vessel has
	 * no classes", draws the plain card, and redraws as a fare table the moment the fares arrive —
	 * which is a flicker on every window and, briefly, the wrong screen.</p>
	 */
	classPricesLoading?: boolean;
	pricesLoading: boolean;
	tripsLoading: boolean;
	/**
	 * Fetches this leg's sailings again. Passed to the cards so joining a waitlist redraws the
	 * sailing with its waitlisted bell — without it the join modal can only say `Refresh the page`.
	 */
	refreshTrips?: () => void;
	currentTab: number;
	setCurrentTab: (date: number) => void;
}

export function DepartureTripSection({
	wizardData,
	startLocation,
	endLocation,
	selectedDepartingTrip,
	trips,
	selectedTrips,
	isStaff,
	priceByFerryTrip,
	classPricesByFerryTrip,
	classPricesLoading,
	pricesLoading,
	tripsLoading,
	refreshTrips,
	currentTab,
	setCurrentTab,
}: DepartureTripSectionProps) {
	const isDepartureTrip = wizardData.departureTrip !== false;
	const partySize = Object.values(passengerCountsByType(wizardData)).reduce((a, b) => a + b, 0);
	const isFreight = wizardData.tabSelected === 'freight';

	// Surface the "must select a departing trip" error on-screen so ScrollToError() has a target
	// when the user clicks Continue without a selection.
	const setWizardData = useBookingWizardDataStore(x => x.setWizardData);

	/**
	 * Whether this sailing is sold by class.
	 *
	 * <p>Classes on <b>and this vessel has some</b>. The setting decides whether the step is a fare
	 * table; the sailing's own fares decide whether it is a row of one. A sailing with none has no
	 * class to name, no upgrade to offer and no fare cell to click, and drawing it as a fare card
	 * gave the booker a card they could not select — as well as handing <c>SelectedFareTrip</c> an
	 * undefined column list to read a chosen class out of.</p>
	 *
	 * <p>It used to also require the classes to differ in price, which meant a vessel whose classes
	 * cost the same was sold as though it had none — the one place the booker chooses between them
	 * stopped offering the choice precisely because the choice was free.</p>
	 */
	const showFareColumns = (trip: TicketsTabTrip) =>
		appConfigStore.passengerClassesEnabled && (classPricesByFerryTrip[trip.id]?.length ?? 0) > 0;

	const baseWizardErrors = useBookingWizardDataStore(x => x.baseWizardErrors);
	const setBaseWizardErrors = useBookingWizardDataStore(x => x.setBaseWizardErrors);
	const showTripError =
		!!baseWizardErrors.departureTicketId && !selectedDepartingTrip && !wizardData.departingJourney;

	// Once a trip is selected, clear the error so it doesn't reappear if the user then deselects it
	// (the error should only come back when they press Continue again without a selection).
	React.useEffect(() => {
		if (wizardData.departingJourney && baseWizardErrors.departureTicketId) {
			setBaseWizardErrors({ ...baseWizardErrors, departureTicketId: undefined });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wizardData.departingJourney]);
	// A sailing this window cannot account for — the route or the direction has changed under it.
	useClearStaleTicket({
		ticketId: wizardData.departureTicketId,
		selectedTrip: selectedDepartingTrip,
		trips,
		loading: tripsLoading || !!classPricesLoading,
		clear: () => {
			const next = new BookingWizardData({ ...wizardData });
			next.departureTicketId = '';
			next.departingJourney = undefined;
			setWizardData(next);
		},
	});

	const selectionClass = showTripError
		? 'ticket-tab__trip-selection ticket-tab__trip-selection--error'
		: 'ticket-tab__trip-selection';

	// Determine which date to display
	const displayDate =
		wizardData.wizardMode === 'ALTERATION' && !isDepartureTrip
			? wizardData.ticketSelectionEndDate
			: wizardData.ticketSelectionStartDate;

	return (
		<>
			<If condition={!isFreight}>
				<h4 className="ticket-tab__sub-header ticket-tab-text">
					{isDepartureTrip ? (
						<FormattedMessage
							id="booking-wizard__tickets__departing-section-title"
							defaultMessage="Departing Trip"
						/>
					) : (
						<FormattedMessage
							id="booking-wizard__tickets__returning-section-title"
							defaultMessage="Returning Trip"
						/>
					)}
				</h4>
				<TripLegSummary
					from={startLocation?.name}
					to={endLocation?.name}
					date={displayDate}
					dateInRail={!selectedDepartingTrip}
				/>
			</If>

			<If condition={isFreight}>
				<h4 className="ticket-tab__sub-header ticket-tab-text">{moment(displayDate).format('DD MMM YYYY')}</h4>
				{/* Freight puts the date in the heading above, so this line carries the route alone. */}
				<TripLegSummary from={startLocation?.name} to={endLocation?.name} />
			</If>

			{/*
				Nothing is drawn until we know whether this vessel sells by class. An empty fare map
				means "not yet" as readily as it means "no classes", and committing to the plain card
				on the strength of it is what made the list flicker from cards to a fare table.
			*/}
			{classPricesLoading && (
				<div className="spinner-container">
					<LottieSpinner className="relative" />
				</div>
			)}

			{/* Show calendar when no trip is selected — wrapped in an error box when the user tried
			    to continue without selecting a trip. */}
			{!classPricesLoading && !selectedDepartingTrip && !wizardData.departureTicketId && (
				<div
					className={selectionClass}
					data-test-id={isDepartureTrip ? 'trip-list-departure' : 'trip-list-return'}
				>
					<If condition={showTripError}>
						<ErrorMessage className="ticket-tab__trip-error" error={baseWizardErrors.departureTicketId} />
					</If>
					<TripSelectionCalendar
						ferryTrips={trips}
						departure={isDepartureTrip}
						selectedTrips={selectedTrips}
						isStaff={isStaff}
						refreshTrips={refreshTrips}
						priceByFerryTrip={priceByFerryTrip}
						classPricesByFerryTrip={classPricesByFerryTrip}
						pricesLoading={pricesLoading}
						tripsLoading={tripsLoading}
						currentTab={currentTab}
						setCurrentTab={setCurrentTab}
					/>
				</div>
			)}

			{/* Show selected trip */}
			{!classPricesLoading && wizardData.departureTicketId && (
				<>
					{!selectedDepartingTrip && (
						<div className="spinner-container">
							<LottieSpinner className="relative" />
						</div>
					)}
					{/*
						Once chosen, the sailing collapses: the two classes the booker passed over
						stop taking the width the one they took needs, and come back as a single
						upgrade line. A vessel without priced classes keeps the card it always had.
					*/}
					{selectedDepartingTrip && showFareColumns(selectedDepartingTrip) && (
						<SelectedFareTrip
							trip={selectedDepartingTrip}
							classPrices={classPricesByFerryTrip[selectedDepartingTrip.id]}
							wizardData={wizardData}
							saveChanges={setWizardData}
							legType={isDepartureTrip ? 'departure' : 'return'}
							from={startLocation?.name}
							to={endLocation?.name}
							alterationDelta={
								wizardData.wizardMode === 'ALTERATION'
									? priceByFerryTrip[selectedDepartingTrip.id] ?? null
									: null
							}
						/>
					)}
					{selectedDepartingTrip && !showFareColumns(selectedDepartingTrip) && (
						<TripOption
							isLoading={pricesLoading}
							trip={selectedDepartingTrip}
							isSelected
							price={priceByFerryTrip[selectedDepartingTrip.id]}
							legType={isDepartureTrip ? 'departure' : 'return'}
						/>
					)}
				</>
			)}
		</>
	);
}
